// Murmurhash is recommended here: https://medium.com/dailymotion/building-a-high-performance-zero-storage-feature-flag-a-b-testing-service-277220444d3a
// Murmurhash is also used by optimizely: https://docs.developers.optimizely.com/full-stack/docs/how-bucketing-works
import murmurhash from "murmurhash";
import _ from "lodash";
import seedrandom from "seedrandom";

const HASH_RANGE = [0, Math.pow(2, 32)] as const; // Murmurhash returns a 32-bit positive integer hash
const NUM_BUCKETS = 1000;
const BUCKET_RANGE = [0, NUM_BUCKETS - 1] as const; // This means our randomization units will land in one of 1000 different buckets

export const HOLDOUT = "holdout" as const;

export type BucketMap<Variant> = Record<number, Variant | typeof HOLDOUT>;

function validateExposurePct(exposurePct: number) {
  if (exposurePct < 0 || exposurePct > 1) {
    throw new Error("exposurePct must be between 0 and 1 (inclusive)");
  }
}

function mapToRange({
  inValue,
  inRange,
  outRange,
  round,
}: {
  inValue: number;
  inRange: readonly [number, number];
  outRange: readonly [number, number];
  round?: boolean;
}) {
  const [inMin, inMax] = inRange;
  const [outMin, outMax] = outRange;
  const mappedVal =
    ((inValue - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
  return round ? Math.round(mappedVal) : mappedVal;
}

// Takes in a randomizationUnitId and returns a bucket number within BUCKET_RANGE
export function getAssignmentBucket(
  randomizationUnitId: string,
  hashSeed: number
): number {
  const hashValue = murmurhash.v3(randomizationUnitId, hashSeed);
  return mapToRange({
    inValue: hashValue,
    inRange: HASH_RANGE,
    outRange: BUCKET_RANGE,
    round: true,
  });
}

export function getBucketMap<Variant>({
  experimentName,
  exposurePct,
  variants,
}: {
  experimentName: string;
  exposurePct: number;
  variants: readonly Variant[];
}) {
  validateExposurePct(exposurePct);
  const allBuckets = _.range(NUM_BUCKETS);
  const seededRNG = seedrandom(experimentName);
  let curIdx = -1;
  // Ripped from lodash's `shuffleSelf` but adding in a seeded random number generator
  // https://github.com/lodash/lodash/blob/ddfd9b11a0126db2302cb70ec9973b66baec0975/lodash.js#L6711
  while (++curIdx < allBuckets.length) {
    const randomNumber = seededRNG();
    const randomIndex = mapToRange({
      inValue: randomNumber,
      inRange: [0, 1],
      outRange: BUCKET_RANGE,
      round: true,
    });
    const value = allBuckets[randomIndex];
    allBuckets[randomIndex] = allBuckets[curIdx];
    allBuckets[curIdx] = value;
  }
  const bucketMap: BucketMap<Variant> = {};
  const variantPcts = 1 / variants.length;
  const holdoutPct = 1 - exposurePct;
  let currentBucketIndex = 0;
  for (let idx = 0; idx < variants.length; idx++) {
    const variant = variants[idx];
    const numBucketsForVariant = Math.floor(variantPcts * NUM_BUCKETS);
    const bucketsForVariant = allBuckets.slice(
      currentBucketIndex,
      currentBucketIndex + numBucketsForVariant
    );
    const numBucketsForHoldout = Math.floor(numBucketsForVariant * holdoutPct);
    let holdoutBuckets = 0;
    for (const bucket of bucketsForVariant) {
      if (holdoutBuckets < numBucketsForHoldout) {
        bucketMap[bucket] = HOLDOUT;
        holdoutBuckets += 1;
      } else {
        bucketMap[bucket] = variant;
      }
    }
    currentBucketIndex += numBucketsForVariant;
  }
  return bucketMap;
}

export function getBucket<Variant>({
  assignmentBucket,
  bucketMap,
}: {
  assignmentBucket: number;
  bucketMap: BucketMap<Variant>;
}): typeof HOLDOUT | Variant {
  if (!bucketMap.hasOwnProperty(assignmentBucket)) {
    return HOLDOUT;
  }
  return bucketMap[assignmentBucket];
}
